import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AboutPage.module.css';

// Default categorized technical skills with icons (synchronized with public/data/skills.json & CV)
const DEFAULT_SKILLS = [
  {
    category: "Programming & Scripting",
    items: [
      { name: "Python", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg" },
      { name: "C", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/c/c-original.svg" },
      { name: "C++", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cplusplus/cplusplus-original.svg" },
      { name: "MATLAB", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/matlab/matlab-original.svg" }
    ]
  },
  {
    category: "Machine Learning & AI",
    items: [
      { name: "TensorFlow", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg" },
      { name: "Keras", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/keras/keras-original.svg" },
      { name: "Scikit-learn", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/scikitlearn/scikitlearn-original.svg" }
    ]
  },
  {
    category: "Engineering Software & CAD",
    items: [
      { name: "SolidWorks 2024", icon: "📐" },
      { name: "ANSYS Mechanical", icon: "⚙️" },
      { name: "ANSYS Fluent", icon: "🌊" }
    ]
  },
  {
    category: "Data Analysis & Simulation",
    items: [
      { name: "NumPy", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/numpy/numpy-original.svg" },
      { name: "Pandas", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pandas/pandas-original.svg" },
      { name: "Matplotlib", icon: "📊" },
      { name: "SPSS", icon: "📈" }
    ]
  },
  {
    category: "Embedded Systems & Hardware",
    items: [
      { name: "Arduino (AVR/ARM)", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/arduino/arduino-original.svg" },
      { name: "Raspberry Pi", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/raspberrypi/raspberrypi-original.svg" }
    ]
  },
  {
    category: "Version Control & Operating Systems",
    items: [
      { name: "Git", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg" },
      { name: "GitHub", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg" },
      { name: "Windows", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/windows8/windows8-original.svg" },
      { name: "Linux (Ubuntu)", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg" }
    ]
  },
  {
    category: "Web Development & Database",
    items: [
      { name: "JavaScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg" },
      { name: "React.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "Node.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg" },
      { name: "Flask", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flask/flask-original.svg" },
      { name: "HTML5", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-original.svg" },
      { name: "CSS3", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg" },
      { name: "Bootstrap", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/bootstrap/bootstrap-original.svg" },
      { name: "MySQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" }
    ]
  },
  {
    category: "Documentation",
    items: [
      { name: "MS Office", icon: "📝" },
      { name: "LaTeX", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/latex/latex-original.svg" }
    ]
  }
];

const CATEGORY_ICONS = {
  "Programming & Scripting": "💻",
  "Programming Languages": "💻",
  "Machine Learning & AI": "🧠",
  "AI & Machine Learning": "🧠",
  "Engineering Software & CAD": "📐",
  "CAD": "📐",
  "Data Analysis & Simulation": "📊",
  "Simulation & Data Analysis": "📊",
  "Embedded Systems & Hardware": "🔌",
  "Embedded Systems": "🔌",
  "Version Control & Operating Systems": "🛠️",
  "Version Control & Tools": "🛠️",
  "Web Development & Database": "🌐",
  "Web & Database Technologies": "🌐",
  "Documentation": "📝"
};

export default function AboutPage({ data }) {
  const navigate = useNavigate();
  const [selectedAward, setSelectedAward] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close certificate modal on Escape key
  useEffect(() => {
    if (!selectedAward) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedAward(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedAward]);

  // HARDCODED AWARDS — guaranteed to render accurately with certificates
  const myAwards = [
    {
      title: "Best Thesis Award 2025",
      result: "🏆 Winner",
      badgeStyle: styles.badgeGold,
      organization: "Aviation and Aerospace University, Bangladesh (AAUB)",
      details: "Awarded for the undergraduate thesis on turbofan engine prognostics: 'A Novel Integrated ML Framework with Comparative Evaluation for Fault Detection, Condition Monitoring, and RUL Prediction of Turbofan Engines.'",
      image: "/assets/certificates/Best Thesis Award-2025.jpeg"
    },
    {
      title: "Best Aerospace Vehicle Design Award 2025",
      result: "🏆 Winner",
      badgeStyle: styles.badgeGold,
      organization: "Aviation and Aerospace University, Bangladesh (AAUB)",
      details: "Sky Sentinel S26, individual aircraft design capstone.",
      image: "/assets/certificates/Best Aerospace Vechicle Design-2025.jpeg"
    },
    {
      title: "AIAA Region VII Student Conference 2025",
      result: "🥈 Tie Second Place (Team Category)",
      badgeStyle: styles.badgeEmerald,
      organization: "American Institute of Aeronautics and Astronautics",
      details: "Sustainable additive manufacturing using recycled PET.",
      image: "/assets/certificates/AIAA_rPET_2nd_Place_2025.png"
    },
    {
      title: "AIAA Region VII Student Conference 2025",
      result: "🥉 Third Place (Team Category)",
      badgeStyle: styles.badgePurple,
      organization: "American Institute of Aeronautics and Astronautics",
      details: "Structural analysis and material selection for a supersonic aircraft wing.",
      image: "/assets/certificates/AIAA_Aircraft_Structure_3rd_Place_2025.png"
    },
    {
      title: "NASA Space Apps Challenge Bangladesh 2023",
      result: "🏅 First Runner-up",
      badgeStyle: styles.badgeBlue,
      organization: "NASA Space Apps Challenge Bangladesh (Rangpur Region)",
      details: "Recognised for developing an innovative solution addressing space and Earth science challenges.",
      image: "/assets/certificates/Nasa_Space_App_Challenge_2023_1st_Runner_Up_certificate.jpg"
    },
    {
      title: "4th Kibo Robot Programming Challenge 2023",
      result: "🥈 Second Place (Bangladesh Region)",
      badgeStyle: styles.badgeEmerald,
      organization: "JAXA / Bangladesh Region",
      details: "Team NIRVEEK; autonomous robot programming for the International Space Station.",
      image: "/assets/certificates/Kibo_Robot_2nd_Place_2023.jpg"
    }
  ];

  // Featured Technical Reports & Design Renders
  const featuredReports = [
    {
      title: "SkyBee: Homeland Defense Interceptor",
      primaryTag: "AIAA 2025",
      primaryTagClass: styles.badgeGold,
      secondaryTag: "Aircraft Design",
      desc: "Complete AIAA 2024-2025 Aircraft Design project. 3 published papers including 3rd Place Team Category winner. Mach 1.6 UCAV with stealth-optimized airframe.",
      image: "/assets/featured/skybee-cover.png",
      linkedinUrl: "https://www.linkedin.com/posts/jolok-banarjee-16825b1b7_aviationandaerospaceuniversitybangladeshdesignreport-ugcPost-7454607313342025728-TQwh/",
      linkedinLabel: "🔗 LinkedIn Post + Report",
      internalSlug: "/projects/skybee-homeland-defense-interceptor"
    },
    {
      title: "Modal Analysis & Mesh Independence",
      primaryTag: "FEA / ANSYS",
      primaryTagClass: styles.badgeEmerald,
      secondaryTag: "Structural Dynamics",
      desc: "Titanium cantilever beam vibration analysis using Euler-Bernoulli theory and ANSYS Workbench. Achieved 0.04% frequency error margin with 1 mm mesh validation.",
      image: "/assets/featured/modal-analysis-cover.png",
      linkedinUrl: "https://www.linkedin.com/posts/jolok-banarjee-16825b1b7_modal-analysis-and-mesh-independence-study-activity-7358457253827371010-eo0A",
      linkedinLabel: "🔗 View on LinkedIn + Report",
      internalSlug: null
    },
    {
      title: "Sky Sentinel S26: Air Defense Interceptor",
      primaryTag: "🏆 Best Design 2025",
      primaryTagClass: styles.badgeGold,
      secondaryTag: "Aerodynamics",
      desc: "Next-generation supersonic interceptor. Mach 2.2 dash, 750nm combat radius, 22°/sec turn rate. Awarded Best Aerospace Vehicle Design 2025.",
      image: "/assets/featured/sky-sentinel-s26-cover.png",
      linkedinUrl: "https://www.linkedin.com/posts/jolok-banarjee-16825b1b7_21014026aircraft-design-sessionalfinal-activity-7352334617594511361-H7dQ",
      linkedinLabel: "🔗 View on LinkedIn + Report",
      internalSlug: "/projects/sky-sentinel-s26"
    }
  ];

  // Safely extract skills from data, preferring rich objects with icons
  const rawSkills = data?.skills?.sections || data?.skills || [];
  const hasRichIcons = Array.isArray(rawSkills) && rawSkills.some(
    (sec) => Array.isArray(sec.items) && sec.items.some((it) => typeof it === 'object' && it.icon)
  );
  const skills = hasRichIcons ? rawSkills : DEFAULT_SKILLS;

  // Bulletproof Path Helper for images
  const getValidPath = (path) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('data:')) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return (process.env.PUBLIC_URL || '') + encodeURI(cleanPath);
  };

  return (
    <div className={styles.container}>

      {/* ── 1. AWARDS & RECOGNITION ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeaderRow}>
          <h2 className={styles.sectionTitle}>Awards & Recognition</h2>
          <span className={styles.sectionCount}>{myAwards.length} Honors</span>
        </div>

        <div className={styles.awardsGrid}>
          {myAwards.map((award, idx) => (
            <article key={idx} className={styles.awardCard}>
              {award.image && (
                <div
                  className={styles.awardImageWrapper}
                  onClick={() => setSelectedAward(award)}
                  title={`Click to view ${award.title} certificate`}
                >
                  <img
                    src={getValidPath(award.image)}
                    alt={`${award.title} Certificate`}
                    className={styles.awardImage}
                    loading="lazy"
                  />
                  <span className={styles.zoomHint}>🔍 Enlarge</span>
                </div>
              )}

              <div className={styles.awardBody}>
                <div className={styles.awardBadgeRow}>
                  {award.result && (
                    <span className={`${styles.awardResult} ${award.badgeStyle || styles.badgeGold}`}>
                      {award.result}
                    </span>
                  )}
                </div>

                <h3 className={styles.awardTitle}>{award.title}</h3>
                <p className={styles.awardOrg}>{award.organization}</p>
                <p className={styles.awardDetails}>{award.details}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── 2. TECHNICAL SKILLS ── */}
      {skills.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeaderRow}>
            <h2 className={styles.sectionTitle}>Research & Technical Skills</h2>
            <span className={styles.sectionCount}>{skills.length} Domains</span>
          </div>

          <div className={styles.skillsGrid}>
            {skills.map((category, idx) => {
              const catName = category.category || category.title || 'Technical Skills';
              const catIcon = CATEGORY_ICONS[catName] || '⚡';
              return (
                <div key={idx} className={styles.skillCategory}>
                  <div className={styles.skillCategoryHeader}>
                    <span className={styles.skillCatIcon} aria-hidden="true">{catIcon}</span>
                    <h3>{catName}</h3>
                  </div>

                  <div className={styles.skillTags}>
                    {category.items.map((item, iIdx) => {
                      const isObj = typeof item === 'object';
                      const name = isObj ? item.name : item;
                      const icon = isObj ? item.icon : null;
                      return (
                        <span key={iIdx} className={styles.skillTag}>
                          {icon && (
                            icon.startsWith('http') || icon.startsWith('/') || icon.startsWith('data:') ? (
                              <img
                                src={icon}
                                alt={name}
                                className={styles.skillIcon}
                                loading="lazy"
                              />
                            ) : (
                              <span className={styles.skillEmoji}>{icon}</span>
                            )
                          )}
                          {name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── 3. FEATURED TECHNICAL REPORTS & DESIGN RENDERS ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeaderRow}>
          <h2 className={styles.sectionTitle}>Featured Technical Reports & Design Renders</h2>
          <span className={styles.sectionCount}>{featuredReports.length} Reports</span>
        </div>

        <div className={styles.reportsGrid}>
          {featuredReports.map((rep, idx) => (
            <article key={idx} className={styles.reportCard}>
              <div className={styles.reportImageWrapper}>
                <img
                  src={getValidPath(rep.image)}
                  alt={rep.title}
                  className={styles.reportImage}
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>

              <div className={styles.reportBody}>
                <div className={styles.reportBadges}>
                  <span className={`${styles.awardResult} ${rep.primaryTagClass}`}>
                    {rep.primaryTag}
                  </span>
                  <span className={styles.reportTagSecondary}>
                    {rep.secondaryTag}
                  </span>
                </div>

                <h3 className={styles.reportTitle}>{rep.title}</h3>
                <p className={styles.reportDesc}>{rep.desc}</p>

                <div className={styles.reportActions}>
                  <a
                    href={rep.linkedinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.reportBtnPrimary}
                  >
                    {rep.linkedinLabel} <span aria-hidden="true">↗</span>
                  </a>
                  {rep.internalSlug && (
                    <button
                      type="button"
                      onClick={() => navigate(rep.internalSlug)}
                      className={styles.reportBtnSecondary}
                    >
                      Project Page →
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── 4. LANGUAGES ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeaderRow}>
          <h2 className={styles.sectionTitle}>Languages</h2>
        </div>

        <div className={styles.languagesGrid}>
          {[
            { lang: 'Bangla', level: 'Native', flag: '🇧🇩' },
            { lang: 'English', level: 'Professional Proficiency — Undergraduate medium of instruction', flag: '🇬🇧' },
          ].map((l, i) => (
            <div key={i} className={styles.languageCard}>
              <span className={styles.languageFlagBadge} aria-hidden="true">{l.flag}</span>
              <div className={styles.languageInfo}>
                <div className={styles.languageName}>{l.lang}</div>
                <div className={styles.languageLevel}>{l.level}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CERTIFICATE LIGHTBOX MODAL ── */}
      {selectedAward && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setSelectedAward(null)}
          role="dialog"
          aria-modal="true"
          aria-label={selectedAward.title}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {selectedAward.title} — {selectedAward.result}
              </h3>
              <button
                type="button"
                className={styles.modalCloseBtn}
                onClick={() => setSelectedAward(null)}
                aria-label="Close certificate preview"
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <img
                src={getValidPath(selectedAward.image)}
                alt={`${selectedAward.title} Certificate`}
                className={styles.modalImage}
              />
              <p className={styles.modalCaption}>
                <strong>{selectedAward.organization}</strong> — {selectedAward.details}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}